import React, { Component, createContext, PropsWithChildren, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useToast } from './ToastManager';

interface ErrorContextType {
  error: AppError | null;
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextType | null>(null);

export const ErrorTypes = {
  VALIDATION: 'VALIDATION_ERROR',
  AUTHENTICATION: 'AUTH_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR',
  GRAPHQL: 'GRAPHQL_ERROR'
} as const;

type ErrorType = typeof ErrorTypes[keyof typeof ErrorTypes];

export class AppError extends Error {
  type: ErrorType;
  originalError: Error | null;
  timestamp: Date;

  constructor(type: ErrorType, message: string, originalError: Error | null = null) {
    super(message);
    this.type = type;
    this.originalError = originalError;
    this.timestamp = new Date();
  }
}

interface ErrorBoundaryProps {
  children: ReactNode;
  onError: (error: Error) => void;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Class component for catching render errors
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Make sure we're catching every possible error
    console.error('Error caught in getDerivedStateFromError:', error);
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught in componentDidCatch:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // Ensure we're handling property access errors properly
    const appError = error instanceof AppError
      ? error
      : new AppError(
          ErrorTypes.UNKNOWN,
          error.message || 'An unexpected rendering error occurred',
          error
        );

    // Make sure the error handler doesn't throw
    try {
      this.props.onError(appError);
    } catch (handlerError) {
      console.error('Error in error handler:', handlerError);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="lp-h-screen lp-flex lp-items-center lp-justify-center lp-bg-black lp-relative lp-overflow-hidden">
          {/* Background glow effect - matching your design */}
          <div className="lp-absolute lp-inset-0 lp-flex lp-items-center lp-justify-center">
            <div
              className="lp-absolute lp-w-[140%] lp-h-[140%]"
              style={{
                background: `
                  radial-gradient(
                    circle at center,
                    rgba(221,184,137,0.08) 0%,
                    rgba(221,184,137,0.03) 20%,
                    rgba(0,0,0,1) 50%
                  )
                `
              }}
            />
          </div>

          {/* Content */}
          <div className="lp-relative lp-z-10 lp-text-center lp-px-4">
            <div className="lp-bg-black/95 lp-p-8 lp-rounded-xl lp-border lp-border-[#DDB889]/10">
              <h2 className="lp-font-boska lp-text-[#DDB889] lp-text-3xl md:lp-text-4xl lp-mb-4">
                Something went wrong
              </h2>
              <p className="lp-text-gray-300 lp-text-lg md:lp-text-xl lp-leading-relaxed lp-mb-8">
                We're having trouble displaying this section.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="lp-bg-black lp-p-3 lp-rounded-xl lp-flex lp-items-center lp-justify-center lp-border lp-border-[#DDB889]/20 lp-text-[#DDB889] hover:lp-scale-105 lp-transition-transform lp-duration-200 lp-mx-auto"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export const ErrorBoundaryProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [globalError, setGlobalError] = useState<AppError | null>(null);
  const showToast = useToast();

  const handleError = useCallback((error: Error) => {
    let appError: AppError;

    if (error instanceof AppError) {
      appError = error;
    } else {
      appError = new AppError(
        ErrorTypes.UNKNOWN,
        'An unexpected error occurred',
        error
      );
    }

    // Log error
    console.error({
      type: appError.type,
      message: appError.message,
      originalError: appError.originalError,
      timestamp: appError.timestamp,
      stack: appError.stack
    });

    // Update global error state
    setGlobalError(appError);

    showToast(appError.message, 'error');

    return appError;
  }, []);

  // Add event listener for custom error events
  useEffect(() => {
    const handleAppError = (event: CustomEvent<AppError>) => {
      handleError(event.detail);
    };

    window.addEventListener('app-error', handleAppError as EventListener);

    return () => {
      window.removeEventListener('app-error', handleAppError as EventListener);
    };
  }, [handleError]);

  const clearError = useCallback(() => {
    setGlobalError(null);
  }, []);

  const value: ErrorContextType = {
    error: globalError,
    clearError
  };

  return (
    <ErrorContext.Provider value={value}>
      <ErrorBoundary onError={handleError}>
        {children}
      </ErrorBoundary>
    </ErrorContext.Provider>
  );
};

// Custom hook to access error context
export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorBoundaryProvider');
  }
  return context;
};
